<template>
  <v-select
    :model-value="modelValue"
    :items="filteredItems"
    :disabled="disabled"
    :menu-props="{ maxHeight: '300' }"
    :placeholder="$t('devices.filters.vulnerability')"
    :data-testid="dataTestId"
    density="compact"
    variant="outlined"
    item-value="value"
    item-title="title"
    multiple
    rounded
    hide-details
    bg-color="white"
    @update:model-value="$emit('update:modelValue', $event)"
    @update:menu="search = ''"
  >
    <template #selection="{ index }">
      <span v-if="index === 0" class="body2">{{ $t("devices.filters.vulnerability") }}</span>
    </template>
    <template #label>
      <span v-if="modelValue?.length" class="filter-label">{{ modelValue.length }}</span>
    </template>
    <template #prepend-item>
      <cui-select-search-field
        v-model="search"
        :placeholder="$t('general.search')"
        :data-test-id="`${dataTestId}-search`"
      />
    </template>
    <template #item="{ props: itemProps }">
      <v-list-item v-bind="itemProps">
        <template #prepend="{ isActive }">
          <v-list-item-action start>
            <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
          </v-list-item-action>
        </template>
      </v-list-item>
    </template>
  </v-select>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import camelCase from "lodash/camelCase";
import { CuiSelectSearchField, useCuiSelectSearch } from "@coronet/cui";
import { DeviceVulnerability } from "@/constants/devices";

/**
 * Device "Vulnerability" multi-select with an in-dropdown search.
 * Consolidates the two copies (DevicesFilters + ExportDevicesFilters). Options come from
 * the `DeviceVulnerability` enum; the visible label (and the search term) resolve through
 * `t('tickets.eventTriggers.<camelCased value>')`.
 */
export default defineComponent({
  components: { CuiSelectSearchField },
  props: {
    /** Selected vulnerability values (v-model). */
    modelValue: {
      type: Array as PropType<DeviceVulnerability[]>,
      default: () => [],
    },
    /** Disables the select. */
    disabled: {
      type: Boolean,
      default: false,
    },
    /** `data-testid` for the select; the search field derives `${dataTestId}-search`. */
    dataTestId: {
      type: String,
      default: "device-vulnerability-filter",
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const { t } = useI18n();

    const allItems = computed(() =>
      Object.values(DeviceVulnerability).map((value) => ({
        value,
        title: t(`tickets.eventTriggers.${camelCase(value)}`),
      }))
    );

    const { search, filteredItems } = useCuiSelectSearch(allItems, (item) => item.title);

    return { search, filteredItems };
  },
});
</script>
